import { anchorFromUrl } from '../types/xlsxAnchor';
import { SearchResult } from '../types/chat';
import { FileSearchResponse } from './chatService.types';

export const sanitizeSearchSnippet = (value?: string): string => {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/<[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Normalize a mode payload (snake_case API -> camelCase UI shape)
export const mapMode = (mode: any) => ({
  id: mode.id,
  name: mode.name,
  description: mode.description,
  color: mode.color,
  icon: mode.icon,
  isActive: mode.is_active,
  isCustom: mode.is_custom,
  sortOrder: mode.sort_order,
  settings: mode.settings,
});

// Map a persisted message source to SearchResult format for SearchResultsCard
export const mapMessageSource = (src: any) => {
  const sourceType = src.sourceType || src.source_type || src.type;
  const sourceUrl = src.sourceUrl || src.source_url || src.url;
  const isWeb = sourceType === 'web';
  return {
    id: src.fileId || src.documentId || src.id || (isWeb ? sourceUrl : '') || '',
    title: src.title || '',
    path: isWeb ? [sourceUrl || ''] : ['General', 'Files'],
    type: isWeb ? 'web' as const : 'document' as const,
    snippet: sanitizeSearchSnippet(src.snippet || src.excerpt || ''),
    excerpt: sanitizeSearchSnippet(src.excerpt || src.snippet || ''),
    fileName: isWeb
      ? src.provider || 'Web'
      : src.fileName || src.file_name || src.title || '',
    sourceUrl,
    provider: src.provider,
    relevance: src.relevance_score || src.relevanceScore || 0.5,
    relevanceScore: src.relevanceScore || src.relevance_score || 0.5,
    rawRelevanceScore: src.rawRelevanceScore ?? src.raw_relevance_score,
    relevanceLabel: src.relevanceLabel ?? src.relevance_label,
    relevanceSource: src.relevanceSource ?? src.relevance_source,
    citationIndex: src.citationIndex ?? src.citation_index,
    selected: src.selected,
    pageNumber: src.pageNumber ?? src.page_number,
    segmentId: src.segmentId ?? src.segment_id,
    sheetName: src.sheetName ?? src.sheet_name,
    cellRef: src.cellRef ?? src.cell_ref,
    cellRange: src.cellRange ?? src.cell_range,
    xlsxAnchor: src.xlsxAnchor ?? src.xlsx_anchor ?? anchorFromUrl(src.sourceUrl || src.url),
    tableBlockId: src.tableBlockId ?? src.table_block_id,
    tableFactId: src.tableFactId ?? src.table_fact_id,
    tableTitle: src.tableTitle ?? src.table_title,
    rowKey: src.rowKey ?? src.row_key,
    columnHeader: src.columnHeader ?? src.column_header,
    valueText: src.valueText ?? src.value_text,
    unit: src.unit,
    aggregation: src.aggregation,
    coverageStatus: src.coverageStatus ?? src.coverage_status,
    coverageReasons: src.coverageReasons ?? src.coverage_reasons,
  };
};

// Map a `search_results` SSE event entry to SearchResult
export const mapStreamSearchResult = (r: any): SearchResult => ({
  id: r.id,
  title: r.title,
  snippet: sanitizeSearchSnippet(r.snippet),
  excerpt: sanitizeSearchSnippet(r.excerpt || r.snippet || ''),
  fileName: r.file_name,
  type: (r.sourceType || r.source_type || r.type) === 'web' ? 'web' : 'document',
  sourceUrl: r.sourceUrl || r.source_url || r.url,
  provider: r.provider,
  relevanceScore: r.relevance_score,
  rawRelevanceScore: r.rawRelevanceScore ?? r.raw_relevance_score,
  relevanceLabel: r.relevanceLabel ?? r.relevance_label,
  relevanceSource: r.relevanceSource ?? r.relevance_source,
  pageNumber: r.pageNumber ?? r.page_number,
  segmentId: r.segmentId ?? r.segment_id,
  sheetName: r.sheetName ?? r.sheet_name,
  cellRef: r.cellRef ?? r.cell_ref,
  cellRange: r.cellRange ?? r.cell_range,
  xlsxAnchor: r.xlsxAnchor ?? r.xlsx_anchor ?? anchorFromUrl(r.sourceUrl || r.url),
  tableBlockId: r.tableBlockId ?? r.table_block_id,
  tableFactId: r.tableFactId ?? r.table_fact_id,
  tableTitle: r.tableTitle ?? r.table_title,
  rowKey: r.rowKey ?? r.row_key,
  columnHeader: r.columnHeader ?? r.column_header,
  valueText: r.valueText ?? r.value_text,
  unit: r.unit,
  aggregation: r.aggregation,
  coverageStatus: r.coverageStatus ?? r.coverage_status,
  coverageReasons: r.coverageReasons ?? r.coverage_reasons,
});

// Map a `sources` SSE event entry to SearchResult
// Note: Backend sends fileId (from PR #167), but also check documentId and id for compatibility
export const mapStreamSource = (s: any): SearchResult => {
  const sourceType = s.sourceType || s.source_type || s.type;
  const sourceUrl = s.sourceUrl || s.source_url || s.url;
  const isWeb = sourceType === 'web';
  return {
    id: s.fileId || s.documentId || s.id || (isWeb ? sourceUrl : undefined),
    title: s.title || '',
    snippet: sanitizeSearchSnippet(s.snippet || s.excerpt || ''),
    excerpt: sanitizeSearchSnippet(s.excerpt || s.snippet || ''),
    fileName: isWeb ? s.provider || 'Web' : s.fileName || s.title || '',
    type: isWeb ? 'web' : 'document',
    sourceUrl,
    provider: s.provider,
    relevanceScore: s.relevanceScore || 0.5,
    rawRelevanceScore: s.rawRelevanceScore,
    relevanceLabel: s.relevanceLabel,
    relevanceSource: s.relevanceSource,
    citationIndex: s.citationIndex,
    selected: s.selected,
    pageNumber: s.pageNumber ?? s.page_number,
    segmentId: s.segmentId ?? s.segment_id,
    sheetName: s.sheetName ?? s.sheet_name,
    cellRef: s.cellRef ?? s.cell_ref,
    cellRange: s.cellRange ?? s.cell_range,
    xlsxAnchor: s.xlsxAnchor ?? s.xlsx_anchor ?? anchorFromUrl(s.sourceUrl || s.url),
    tableBlockId: s.tableBlockId ?? s.table_block_id,
    tableFactId: s.tableFactId ?? s.table_fact_id,
    tableTitle: s.tableTitle ?? s.table_title,
    rowKey: s.rowKey ?? s.row_key,
    columnHeader: s.columnHeader ?? s.column_header,
    valueText: s.valueText ?? s.value_text,
    unit: s.unit,
    aggregation: s.aggregation,
    coverageStatus: s.coverageStatus ?? s.coverage_status,
    coverageReasons: s.coverageReasons ?? s.coverage_reasons,
  };
};

// Map a file-search API result entry to SearchResult
export const mapFileSearchResult = (
  result: FileSearchResponse['results'][number]
): SearchResult => ({
  id: result.id,
  title: result.title,
  snippet: sanitizeSearchSnippet(result.snippet),
  fileName: result.file_name,
  relevanceScore: result.relevance_score,
  pageNumber: result.pageNumber ?? result.page_number,
  segmentId: result.segmentId ?? result.segment_id,
  sheetName: result.sheetName ?? result.sheet_name,
  cellRef: result.cellRef ?? result.cell_ref,
  cellRange: result.cellRange ?? result.cell_range,
  tableBlockId: result.tableBlockId ?? result.table_block_id,
  tableFactId: result.tableFactId ?? result.table_fact_id,
  tableTitle: result.tableTitle ?? result.table_title,
  rowKey: result.rowKey ?? result.row_key,
  columnHeader: result.columnHeader ?? result.column_header,
  valueText: result.valueText ?? result.value_text,
  unit: result.unit,
});
